import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';

const SUPPORT_EMAIL = 'support@citizenly.net';
const LAST_UPDATED = 'May 12, 2026';
const GOVERNING_LAW_STATE = '[STATE]';
const ease = [0.22, 1, 0.36, 1] as const;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display font-light text-text-primary text-3xl md:text-4xl leading-tight mb-5">
        {title}
      </h2>
      <div className="font-sans text-base md:text-[17px] text-text-secondary leading-[1.75] space-y-4">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-cream">
      <LandingNav />

      <main className="mx-auto w-full max-w-3xl px-6 md:px-8 pt-16 pb-24 md:pt-24 md:pb-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-primary/60" />
          <span className="font-sans text-xs text-text-secondary uppercase tracking-wider">
            Terms
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05, ease }}
          className="font-display font-light text-text-primary text-5xl md:text-[64px] leading-[1.05] tracking-[-0.01em] mb-5"
        >
          Terms of <span className="italic">Service</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease }}
          className="font-sans text-sm text-text-muted mb-12"
        >
          Last updated {LAST_UPDATED}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease }}
          className="space-y-14"
        >
          <Section id="acceptance" title="1. Acceptance of these terms">
            <p>
              These Terms of Service (&ldquo;Terms&rdquo;) form a binding
              agreement between you and Citizenly (&ldquo;Citizenly,&rdquo;
              &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By
              creating an account, subscribing, or otherwise using citizenly.net
              (the &ldquo;Service&rdquo;), you agree to these Terms and to our{' '}
              <Link
                to="/privacy"
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                Privacy Policy
              </Link>
              . If you do not agree, please do not use the Service.
            </p>
          </Section>

          <Section id="service" title="2. What Citizenly is">
            <p>
              Citizenly is an AI-assisted practice tool that helps you prepare
              for the USCIS naturalization interview. The Service provides
              civics questions, mock interviews, voice-graded answers, and
              progress tracking.
            </p>
            <p>
              <strong className="text-text-primary">
                Citizenly is not affiliated with USCIS or any U.S. government
                agency.
              </strong>{' '}
              We do not file applications, schedule interviews, or make any
              decisions about your immigration status. Nothing in the Service is
              legal advice. If you need legal advice about your immigration
              case, consult a licensed immigration attorney or a U.S. Department
              of Justice-accredited representative.
            </p>
          </Section>

          <Section id="eligibility" title="3. Who can use Citizenly">
            <p>
              You must be at least 13 years old to use the Service, and at
              least 18 (or the age of majority where you live) to purchase a
              paid plan. By using the Service, you represent that you meet
              these requirements and that you are able to enter into a binding
              contract.
            </p>
          </Section>

          <Section id="accounts" title="4. Your account">
            <p>
              You&apos;re responsible for keeping your sign-in credentials safe
              and for everything that happens under your account. Each account
              is for a single individual; please don&apos;t share it. Notify us
              at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              if you suspect unauthorized access.
            </p>
            <p>
              We may suspend or terminate accounts that contain false
              information or that are used in violation of these Terms.
            </p>
          </Section>

          <Section id="subscriptions" title="5. Subscriptions, billing, and refunds">
            <p>
              Citizenly offers a free tier with usage limits and paid plans
              billed monthly or annually. Paid plans renew automatically at the
              end of each billing period at the then-current price unless you
              cancel before renewal. You can cancel any time from your account
              settings, which opens the Stripe billing portal.
            </p>
            <p>
              <strong className="text-text-primary">Refunds:</strong> Except
              where required by law, subscription fees are non-refundable, and
              we do not provide refunds or credits for partial billing periods,
              unused time, or features you didn&apos;t use. If we materially
              reduce features you paid for, you may contact{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              for a prorated refund.
            </p>
            <p>
              We may change pricing for future billing periods. If we do,
              we&apos;ll notify you in advance so you can decide whether to
              continue. Payments are processed by Stripe, and you agree to
              Stripe&apos;s terms when you provide payment information.
            </p>
          </Section>

          <Section id="trial" title="6. Free trial">
            <p>
              We may offer a free trial of a paid plan. Unless you cancel
              before the trial ends, your subscription will start automatically
              and your payment method will be charged at the regular price. You
              can cancel the trial at any time from your account settings.
            </p>
          </Section>

          <Section id="acceptable-use" title="7. Acceptable use">
            <p>While using Citizenly, you agree not to:</p>
            <ul className="space-y-2 list-disc pl-6 marker:text-primary">
              <li>Scrape, copy, or republish our content without permission.</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service.</li>
              <li>Use the Service to build or train a competing product.</li>
              <li>Share your account or sell access to it.</li>
              <li>Upload anything illegal, harmful, hateful, or that infringes someone else&apos;s rights.</li>
              <li>Interfere with the Service, attempt to bypass usage limits, or use bots or automated scripts.</li>
              <li>Use the Service to harass, defraud, or impersonate anyone.</li>
            </ul>
          </Section>

          <Section id="user-content" title="8. Your content">
            <p>
              You retain ownership of the voice recordings, transcripts, and
              other inputs you provide to Citizenly (&ldquo;Your Content&rdquo;).
              You grant us a limited, worldwide, royalty-free license to
              process Your Content for the sole purpose of operating, providing,
              and improving the Service &mdash; for example, to transcribe your
              audio, grade your answers, and show you progress over time. This
              license ends when you delete Your Content or your account, except
              for content we&apos;re legally required to retain or that exists
              in routine backups.
            </p>
            <p>
              We may use de-identified, aggregated information derived from
              Your Content to improve the Service. This data cannot be used to
              identify you.
            </p>
          </Section>

          <Section id="ai-disclaimer" title="9. AI-generated results">
            <p>
              Citizenly uses automated transcription and AI to score your
              answers and surface suggestions. These outputs are{' '}
              <strong className="text-text-primary">estimates, not guarantees</strong>
              . They can be wrong, especially on accents, names, places, and
              edge-case questions. A high practice score does not guarantee
              that you will pass your real interview, and a low score does not
              mean you will fail.
            </p>
            <p>
              <strong className="text-text-primary">
                Do not rely on Citizenly for legal or immigration decisions.
              </strong>{' '}
              Always consult a qualified attorney or accredited representative
              before taking action on your case.
            </p>
          </Section>

          <Section id="ip" title="10. Our intellectual property">
            <p>
              The Service, including its software, content, questions, audio
              prompts, branding, and design, is owned by Citizenly or our
              licensors and is protected by intellectual property laws. We grant
              you a limited, non-exclusive, non-transferable, revocable license
              to use the Service for your personal preparation, subject to
              these Terms.
            </p>
          </Section>

          <Section id="third-parties" title="11. Third-party services">
            <p>
              The Service relies on third-party providers (such as Stripe for
              payments and Deepgram for transcription). Your use of those
              services is governed by their own terms and privacy policies, and
              we are not responsible for them. We list our processors in our{' '}
              <Link
                to="/privacy"
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section id="termination" title="12. Suspension and termination">
            <p>
              You can stop using the Service and delete your account at any
              time. We may suspend or terminate your access if you violate
              these Terms, if we reasonably believe your use creates legal or
              safety risk, or if we discontinue the Service. If we terminate
              you for cause, we may do so without refund.
            </p>
          </Section>

          <Section id="disclaimers" title="13. Disclaimers">
            <p className="uppercase tracking-wide text-sm md:text-[15px]">
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as
              available,&rdquo; without warranties of any kind, whether
              express, implied, statutory, or otherwise. To the maximum extent
              permitted by law, Citizenly disclaims all warranties, including
              merchantability, fitness for a particular purpose,
              non-infringement, and any warranty arising out of course of
              dealing or usage of trade. We do not warrant that the Service
              will be uninterrupted, error-free, or that any output will be
              accurate or sufficient to pass your interview.
            </p>
          </Section>

          <Section id="liability" title="14. Limitation of liability">
            <p className="uppercase tracking-wide text-sm md:text-[15px]">
              To the maximum extent permitted by law, Citizenly and its
              officers, directors, employees, and agents will not be liable for
              any indirect, incidental, special, consequential, exemplary, or
              punitive damages, or for any loss of profits, revenues, data, or
              goodwill, arising from or related to your use of the Service.
              Our total liability for any claim arising out of or related to
              these Terms or the Service will not exceed the greater of (a) the
              amount you paid Citizenly in the twelve months before the claim
              arose, or (b) one hundred U.S. dollars (US $100).
            </p>
          </Section>

          <Section id="indemnification" title="15. Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless Citizenly from
              and against any claims, damages, liabilities, and expenses
              (including reasonable attorneys&apos; fees) arising out of (a)
              your use of the Service, (b) Your Content, or (c) your violation
              of these Terms or applicable law.
            </p>
          </Section>

          <Section id="governing-law" title="16. Governing law and disputes">
            <p>
              These Terms are governed by the laws of the State of{' '}
              {GOVERNING_LAW_STATE}, without regard to its conflict of laws
              rules. Before filing any formal claim, you agree to first contact
              us at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              and try to resolve the dispute informally for at least 30 days.
            </p>
            <p>
              If we can&apos;t resolve a dispute informally, you and Citizenly
              agree that any dispute will be resolved exclusively in the state
              or federal courts located in {GOVERNING_LAW_STATE}, and we each
              consent to personal jurisdiction there. You and Citizenly each
              waive the right to a jury trial and the right to participate in a
              class action.
            </p>
          </Section>

          <Section id="changes" title="17. Changes to these terms">
            <p>
              We may update these Terms from time to time. If we make a
              material change, we&apos;ll let you know by email or in the app.
              Your continued use of the Service after the change takes effect
              means you accept the updated Terms. If you do not accept them,
              you may stop using the Service and cancel your subscription.
            </p>
          </Section>

          <Section id="contact" title="18. Contact us">
            <p>
              Questions about these Terms? Email us at{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary underline underline-offset-4 hover:text-primary-dark transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </motion.div>
      </main>

      <LandingFooter />
    </div>
  );
}
